import {LobbyImage, TimeoutId} from '@colonist/types'
import {UIReplayButton} from './UIReplayButton'
import {createButtonIn, createDivIn} from '../../ui_utils/DomUtils'
import {UIGameFeed} from '../../ui_game/ui_game_feed/UIGameFeed'
import {UIReplayAvatarView} from './UIReplayAvatarView'
import {UIReplaySlider} from './UIReplaySlider'
import {GameClientUserState, ReplayInitiationData} from '@colonist/game-base-types'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {UIReplayController} from '../UIReplayController'
import {GameEventHistory, PlayerColor} from '@colonist/game-states'
import {uiGameManager} from '../../EntryShared'
import {getHighlightsFromEventHistory} from '../EventHistoryUtils'
import {AnalyticEventType, AnalyticEventCategory} from '../../../../../shared/entity_datas/EntityDatas'
import {ErrorReplay} from '../../../../../shared/errors/ErrorsReplay'
import {CA} from '../../analytics/Analytics'
import {UIErrorHandler} from '../../errors/UIErrorHandler'
import {TranslationObject} from '@colonist/i18n'
import {UIReplayShareButton} from './UIReplayShareButton'
import {KeyBindController, ShortcutLocation} from '../../KeyBindController'

enum Velocity {
    X05 = 0.5,
    X1 = 1,
    X2 = 2,
    X4 = 4,
    X8 = 8
}
export class UIReplayView extends UIGameFeed {
    protected readonly uiGameManager: UIGameManager
    readonly replayController: UIReplayController

    private previousTurnButton?: UIReplayButton
    private previousActionButton?: UIReplayButton
    private velocityButton?: UIReplayButton
    private playPauseButton?: UIReplayButton
    private nextActionButton?: UIReplayButton
    private nextTurnButton?: UIReplayButton
    private slider?: UIReplaySlider
    openStatsButton?: HTMLButtonElement
    private readonly avatarViews: UIReplayAvatarView[] = []

    private expandedContainer: HTMLDivElement

    private readonly totalNumberOfActions: number
    private readonly playerUserStates: GameClientUserState[]

    private expandIcon: UIReplayButton

    private isExpanded = false
    private isPlaying = false
    private actionInterval: TimeoutId | undefined
    private velocity: Velocity = Velocity.X1
    private msPerAction = 1000

    constructor(replayController: UIReplayController, useMinimalControls: boolean = false, isPortrait: boolean = false) {
        super('replay-view', 'replay-view-main-block', 'replay-view-container')
        this.mainBlock.hidden = false

        this.uiGameManager = uiGameManager
        this.replayController = replayController

        const replayInitiationData = replayController.replayData
        const initialState = 1
        this.totalNumberOfActions = replayInitiationData.eventHistory.events.length + initialState
        this.playerUserStates = replayInitiationData.playerUserStates

        if(useMinimalControls) this.buildReplayContainerMinimal(replayInitiationData, replayController.getCurrentAction(), isPortrait)
        else this.buildReplayContainer(replayInitiationData, replayController.getCurrentAction())

        this.mainBlock.addEventListener('click', () => {
            void CA.sendAnalyticEventOnce({
                type: AnalyticEventType.ReplayControlsFirstInteraction,
                category: AnalyticEventCategory.Replay,
                floatValue: this.replayController.replayAccessMethod,
            })
        })

        window.addEventListener('resize', () => {
            this.setPosition()
        })
        this.setPosition()
    }

    // eslint-disable-next-line class-methods-use-this
    setPosition() {
        return
    }

    private buildReplayContainer(replayInitiationData: ReplayInitiationData, startingActionIndex: number) {
        const replayControlsContainer = createDivIn(this.container, 'replay-controls-container')
        replayControlsContainer.classList.add('flex-grow-content')

        this.createPreviousTurnButton(replayControlsContainer)
        this.createPreviousActionButton(replayControlsContainer)
        this.createPlayPauseButton(replayControlsContainer)
        this.createVelocityButton(replayControlsContainer)
        this.createNextActionButton(replayControlsContainer)
        this.createNextTurnButton(replayControlsContainer)

        this.createSlider(replayInitiationData.eventHistory, startingActionIndex)

        this.expandedContainer = createDivIn(this.container, 'replay-expanded-container')
        this.expandedContainer.style.display = this.isExpanded ? 'flex' : 'none'

        this.createExpandIcon(replayControlsContainer)
        this.createAvatars(this.expandedContainer, replayInitiationData)
        createDivIn(this.expandedContainer, 'replay-controls-separator')
        this.createShareButton()
        this.expandAction()
        this.buildOpenStatsContainer()
    }

    private buildReplayContainerMinimal(replayInitiationData: ReplayInitiationData, startingActionIndex: number, isPortrait: boolean) {
        this.createSlider(replayInitiationData.eventHistory, startingActionIndex)

        const replayControlsContainer = createDivIn(this.container, 'replay-controls-container')
        this.createPreviousActionButton(replayControlsContainer)
        this.createPlayPauseButton(replayControlsContainer)
        this.createNextActionButton(replayControlsContainer)
        createDivIn(replayControlsContainer, 'replay-controls-separator')
        this.createAvatars(replayControlsContainer, replayInitiationData)

        if(!isPortrait) this.buildOpenStatsContainer()
        else {
            this.replayController.createEndGameScreen()
            this.replayController.hideEndGameScreen()
        }
    }

    private buildOpenStatsContainer() {
        const text: TranslationObject = {key: 'strings:game.endGame.cornerButton.openStats'}
        const openStatsContainer = createDivIn(this.mainBlock, 'replay-open-stats-container')
        const openStatsButtonContainer = createDivIn(openStatsContainer, 'replay-open-stats-button-container')
        this.openStatsButton = createButtonIn(openStatsButtonContainer, 'replay-open-stats-button', text)

        this.openStatsButton.onclick = () => {
            const actionIndex = this.replayController.actionIndexForEndgame
            this.replayController.setAction(actionIndex, undefined, false)

            void CA.sendAnalyticEventOnce({
                type: AnalyticEventType.ReplayClickedOpenStats,
                category: AnalyticEventCategory.Replay,
                floatValue: this.replayController.replayAccessMethod,
            })
        }
    }

    private changePerspective(playerColor: PlayerColor) {
        void CA.sendAnalyticEvent({
            type: AnalyticEventType.ReplayClickedChangePerspective,
            category: AnalyticEventCategory.Replay,
            floatValue: this.replayController.replayAccessMethod,
        })

        const isPerspectiveAvailable = this.replayController.isPlayerPerspectiveAvailable(playerColor)
        if(!isPerspectiveAvailable) return UIErrorHandler.handleError(new ErrorReplay.NoMembershipTryingToChangeReplayPerspective())

        this.replayController.changePlayerPerspective(playerColor)
        this.updateFocusedPlayerAvatar(playerColor)
        return
    }

    private expandAction() {
        this.isExpanded = !this.isExpanded
        this.expandIcon.setAsset(this.isExpanded ? LobbyImage.UpArrow : LobbyImage.DownArrow)
        this.expandedContainer.style.display = this.isExpanded ? 'flex' : 'none'
        this.slider?.setHighlightsVisible(this.isExpanded)
    }

    private setAction(actionIndex: number) {
        if(this.isPlaying) this.pause()
        this.replayController.setAction(actionIndex, undefined, false)
    }

    private pause() {
        this.isPlaying = false
        this.playPauseButton?.setAsset(LobbyImage.Play)
        clearInterval(this.actionInterval)
    }

    private play() {
        this.isPlaying = true
        this.playPauseButton?.setAsset(LobbyImage.Pause)
        this.actionInterval = setInterval(() => this.replayController.nextAction(), this.msPerAction)
    }

    updateActionIndex(actionIndex: number) {
        const shiftedIndex = actionIndex + 1
        this.slider?.setAction(actionIndex)
        if(shiftedIndex === 0) {
            this.previousTurnButton?.setDisabled(true)
            this.previousActionButton?.setDisabled(true)
        } else {
            this.previousTurnButton?.setDisabled(false)
            this.previousActionButton?.setDisabled(false)
        }

        const finalActionIndex = this.totalNumberOfActions - 1
        if(shiftedIndex === finalActionIndex) {
            this.nextActionButton?.setDisabled(true)
            this.nextTurnButton?.setDisabled(true)
            this.pause()
        } else {
            this.nextActionButton?.setDisabled(false)
            this.nextTurnButton?.setDisabled(false)
        }
    }

    updateFocusedPlayerAvatar(playerColor: PlayerColor) {
        for(const avatar of this.avatarViews) {
            avatar.setUnfocused(avatar.playerColor != playerColor)
        }
    }

    private static nextVelocity(velocity: Velocity): Velocity {
        switch(velocity) {
            case Velocity.X05: return Velocity.X1
            case Velocity.X1: return Velocity.X2
            case Velocity.X2: return Velocity.X4
            case Velocity.X4: return Velocity.X8
            case Velocity.X8: return Velocity.X05
        }
    }

    private static velocityEnumToAsset(velocity: Velocity): LobbyImage {
        switch(velocity) {
            case Velocity.X05: return LobbyImage.X05
            case Velocity.X1: return LobbyImage.X1
            case Velocity.X2: return LobbyImage.X2
            case Velocity.X4: return LobbyImage.X4
            case Velocity.X8: return LobbyImage.X8
        }
    }

    private static velocityEnumToMs(velocity: Velocity): number {
        switch(velocity) {
            case Velocity.X05: return 2000
            case Velocity.X1: return 1000
            case Velocity.X2: return 500
            case Velocity.X4: return 250
            case Velocity.X8: return 125
        }
    }

    private createAvatars(container: HTMLDivElement, replayInitiationData: ReplayInitiationData) {
        const initialGameState = replayInitiationData.eventHistory.initialState
        for(const playerColorString in initialGameState.playerStates) {
            const playerColor = Number(playerColorString) as PlayerColor
            const player = initialGameState.playerStates[playerColor]
            if(player == undefined) continue

            const userState = this.playerUserStates.find((playerUserState) => playerUserState.selectedColor === playerColor)
            if(userState == undefined) continue

            const tooltip: TranslationObject = {key: 'strings:game.tooltips.replay.changePerspective', options: {playerColor: PlayerColor[player.color]}}
            const avatar = new UIReplayAvatarView(container, userState.databaseIcon, player.color, tooltip, userState)
            avatar.setAction(() => this.changePerspective(playerColor))
            avatar.setUnfocused(player.color != this.uiGameManager.gameController.myColor)
            this.avatarViews.push(avatar)
        }
    }

    private createExpandIcon(replayControlsContainer: HTMLDivElement) {
        const expandIconAsset = this.isExpanded ? LobbyImage.UpArrow : LobbyImage.DownArrow
        this.expandIcon = new UIReplayButton(replayControlsContainer, expandIconAsset, () => this.expandAction(), {small: true})
    }

    private createShareButton() {
        const shareIconContainer = createDivIn(this.expandedContainer, 'replay-share-container')
        new UIReplayShareButton(this.replayController, shareIconContainer)
    }

    private createSlider(eventHistory: GameEventHistory, startingActionIndex: number) {
        this.slider = new UIReplaySlider(this.container, {key: 'strings:colonistCommon.utils.emptyString'}, startingActionIndex, this.totalNumberOfActions, (action) => this.setAction(action))

        const highlightData = getHighlightsFromEventHistory(eventHistory)
        for(const highlight of highlightData) {
            this.slider.addHighlight(highlight)
        }
    }

    private createVelocityButton(container: HTMLDivElement) {
        const toggleVelocity = () => {
            const nextVelocity = UIReplayView.nextVelocity(this.velocity)
            this.velocity = nextVelocity
            this.velocityButton?.setAsset(UIReplayView.velocityEnumToAsset(nextVelocity))
            this.msPerAction = UIReplayView.velocityEnumToMs(nextVelocity)

            if(this.isPlaying) {
                clearInterval(this.actionInterval)
                this.actionInterval = setInterval(() => this.replayController.nextAction(), this.msPerAction)
            }
        }

        const velocityIcon = UIReplayView.velocityEnumToAsset(this.velocity)
        this.velocityButton = new UIReplayButton(container, velocityIcon, toggleVelocity, {tooltip: {key: 'strings:game.tooltips.replay.velocity'}})
    }

    private createPlayPauseButton(container: HTMLDivElement) {
        const togglePlayPause = () => {
            this.isPlaying ? this.pause() : this.play()
        }

        this.playPauseButton = new UIReplayButton(container, LobbyImage.Play, togglePlayPause, {tooltip: {key: 'strings:game.tooltips.replay.playPause'}})

        const keyBindDelayInMS = 0
        const withoutShift = false
        KeyBindController.bindKey('Space', togglePlayPause, () => true, ShortcutLocation.InGame, withoutShift, keyBindDelayInMS)
    }

    private createNextActionButton(container: HTMLDivElement) {
        const nextAction = () => {
            if(this.isPlaying) this.pause()
            this.replayController.nextAction()
        }

        this.nextActionButton = new UIReplayButton(container, LobbyImage.FastForward, nextAction, {tooltip: {key: 'strings:game.tooltips.replay.nextAction'}})
    }

    private createPreviousActionButton(container: HTMLDivElement) {
        const previousAction = () => {
            if(this.isPlaying) this.pause()
            this.replayController.previousAction()
        }

        this.previousActionButton = new UIReplayButton(container, LobbyImage.Rewind, previousAction, {tooltip: {key: 'strings:game.tooltips.replay.previousAction'}})
    }

    private createNextTurnButton(container: HTMLDivElement) {
        const nextTurn = () => {
            if(this.isPlaying) this.pause()
            this.replayController.nextTurn()
        }

        this.nextTurnButton = new UIReplayButton(container, LobbyImage.FastForwardToEnd, nextTurn, {tooltip: {key: 'strings:game.tooltips.replay.nextTurn'}})
    }

    private createPreviousTurnButton(container: HTMLDivElement) {
        const previousTurn = () => {
            if(this.isPlaying) this.pause()
            this.replayController.previousTurn()
        }

        this.previousTurnButton = new UIReplayButton(container, LobbyImage.RewindToStart, previousTurn, {tooltip: {key: 'strings:game.tooltips.replay.previousTurn'}})
    }
}
